import axios from 'axios';
import type { Club, Team, CreateTeamData, UpdateClubData } from '../types/club';
import { API_BASE_URL } from '../config/api';

// Custom error class for better error handling
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Auth token helper function
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null; // SSR check
  
  try {
    const token = localStorage.getItem('auth-storage');
    if (!token) return null;
    
    const authData = JSON.parse(token);
    return authData?.state?.token || null;
  } catch (error) {
    console.error('Error parsing auth token:', error);
    localStorage.removeItem('auth-storage');
    return null;
  }
};

// Create axios instance with enhanced configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - centralized error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.mensaje || 'Error desconocido';
    const code = error.response?.data?.codigo || 'UNKNOWN_ERROR';
    
    // Handle specific error cases
    if (status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-storage');
        // window.location.href = '/login';
      }
    }
    
    throw new ApiError(status, code, message);
  }
);

// Club service with improved error handling
export const clubService = {
  /**
   * Get current user's club information
   */
  async getMyClub(): Promise<Club> {
    const response = await api.get('/clubes/mi-club');
    return response.data.club;
  },

  /**
   * Update current user's club information
   */
  async updateMyClub(data: UpdateClubData): Promise<Club> {
    // Basic validation for UpdateClubData
    if (data.nombre !== undefined && !data.nombre?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El nombre del club no puede estar vacío');
    }
    
    if (data.email !== undefined && data.email && !isValidEmail(data.email)) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El email no es válido');
    }
    
    const response = await api.put('/clubes/mi-club', data);
    return response.data.club;
  },

  /**
   * Get all teams for the current club
   */
  async getClubTeams(): Promise<Team[]> {
    const response = await api.get('/clubes/equipos');
    return response.data.equipos;
  },

  /**
   * Create a new team for the current club
   */
  async createTeam(data: CreateTeamData): Promise<Team> {
    // Basic validation for CreateTeamData
    if (!data.nombre?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El nombre del equipo es requerido');
    }
    
    if (!data.categoria?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'La categoría del equipo es requerida');
    }
    
    if (!data.temporada?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'La temporada es requerida');
    }
    
    const response = await api.post('/clubes/equipos', data);
    return response.data.equipo;
  },

  /**
   * Create a new club
   */
  async createClub(data: Partial<Club>): Promise<Club> {
    // Basic validation for Club creation
    if (!data.nombre?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El nombre del club es requerido');
    }
    
    if (!data.nombre_corto?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El nombre corto del club es requerido');
    }
    
    if (data.email && !isValidEmail(data.email)) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El email no es válido');
    }
    
    const response = await api.post('/clubes', data);
    return response.data.club;
  },

  /**
   * Delete a team
   */
  async deleteTeam(teamId: string): Promise<void> {
    if (!teamId?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El ID del equipo es requerido');
    }
    
    await api.delete(`/clubes/equipos/${teamId}`);
  },

  /**
   * Update team information
   */
  async updateTeam(teamId: string, data: Partial<CreateTeamData>): Promise<Team> {
    if (!teamId?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El ID del equipo es requerido');
    }
    
    // Validate only provided fields
    if (data.nombre !== undefined && !data.nombre?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El nombre del equipo no puede estar vacío');
    }
    
    if (data.categoria !== undefined && !data.categoria?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'La categoría no puede estar vacía');
    }
    
    if (data.temporada !== undefined && !data.temporada?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'La temporada no puede estar vacía');
    }
    
    const response = await api.put(`/clubes/equipos/${teamId}`, data);
    return response.data.equipo;
  },

  /**
   * Get a specific team by ID
   */
  async getTeam(teamId: string): Promise<Team> {
    if (!teamId?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El ID del equipo es requerido');
    }
    
    const response = await api.get(`/clubes/equipos/${teamId}`);
    return response.data.equipo;
  },

  /**
   * Get detailed team information including players
   */
  async getTeamDetails(teamId: string): Promise<any> {
    if (!teamId?.trim()) {
      throw new ApiError(400, 'VALIDATION_ERROR', 'El ID del equipo es requerido');
    }
    
    try {
      const response = await api.get(`/clubes/equipos/${teamId}`);
      return response.data.equipo;
    } catch (error) {
      console.error('Error getting team details:', error);
      throw error;
    }
  },
};

// Helper function for email validation
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Export the api instance for advanced use cases
export { api };
